import { synthCombiner, synthPost, synthPre, synthSymbols } from "../speak/exact";
import { getAllIpa, getAllModifiers, getFeaturesForOne, ipaStress, isStress, isTone } from "./ipa";

export interface PhonemeList {
  readonly type: 'ipa' | 'synth';
  readonly hasUnknown: boolean;
  readonly phonemes: PhonemeItem[];
}
export interface PhonemeItem {
  readonly phoneme: string;
  readonly meta?: MetaType;
}
export type MetaType = 'unknown' | 'space' | 'newline'
  | 'stress'  /** primary or secondary stress */
  | 'pause'   /** a pause such as a comma or period */
  | 'break';  /** syllable break */

export type SegmentType =
  | 'ipa'         /** IPA with diacritics and tie-bars */
  | 'ipaModifier' /** IPA with modifiers like ʰ */
  | 'ipaPlus'     /** IPA with length numbers */
  | 'synth';      /** synth encoding */

/**
 * segment a string of phonemes into individual phonemes,
 * which might include diacritics, default to 'ipa',
 * optionally include modifier symbols (like (like ʰ) with 'ipaModifier' or length numbers with 'ipaPlus',
 * optionally treating all affricates (like dz) as a single phoneme, or just English ones,
 */
export function segmentPhonemes(
  input: string, theType?: SegmentType, affricates?: 'all-aff' | 'en-aff'
): PhonemeList {
  const validator = PhonemeValidator.get();
  const type = theType === 'ipaModifier' || theType === 'ipaPlus' ? 'ipa' : theType ?? 'ipa';
  const modifier = theType === 'ipaModifier';
  const plus = theType === 'ipaPlus';

  const tracker: TrackCurrent = new TrackCurrent();
  let i = -1;
  for (const s of input) {
    i++;

    if (type !== 'synth' && validator.isDelimiter(s)) {
      continue;
    } else if (s === ' ') {
      tracker.set(s, 'space');
      continue;
    } if (s === '\n') {
      tracker.set(s, 'newline');
      continue;
    } else if (ipaStress.includes(s)) {
      tracker.useCurrent();
      tracker.set(s, 'stress');
      continue;
    } else if (s === '.' && type === 'ipa') {
      tracker.addDot();
      continue;
    } else if (s === ',' && type === 'ipa') {
      tracker.useCurrent();
      tracker.set(',', 'pause');
      continue;
    }

    const isSynthSymbol = type === 'synth' && validator.isSynthSymbol(s);
    const isSynthDiacritic = type === 'synth' && validator.isSynthDiacritic(s);
    const isSynthPrefix = type === 'synth' && validator.isSynthPrefix(s);
    const isSynth = isSynthSymbol || isSynthDiacritic || isSynthPrefix;
    const wasCombiner = type === 'synth' && input[i - 1] === synthCombiner;
    const isIpaSymbol = type === 'ipa' && validator.isIpaSymbol(s, plus);
    const isIpaDiacritic1 = type === 'ipa' && validator.isIpaDiacritic(s);
    const isIpaDiacritic = isIpaDiacritic1 && tracker.currentCanHaveDiacritic();
    const isIpa = isIpaSymbol || isIpaDiacritic1;

    // add in previous phoneme unless we're adding diacritics or adding to a prefix
    if (!isSynthDiacritic && !isIpaDiacritic && tracker.hasCurrent() && !tracker.hasPrefix() && !wasCombiner) {
      tracker.useCurrent();
    }

    // detect type & validity
    let valid = true;
    if (type === 'synth' && !isSynth) {
      valid = false;
    } else if (type === 'ipa' && !isIpa) {
      valid = false;
    }

    // track current phoneme & invalid symbols
    if (!valid) {
      tracker.set(s, 'unknown');
    } else if (isSynthDiacritic || isIpaDiacritic || wasCombiner) {
      tracker.add(s);
    } else if (isSynthPrefix) {
      tracker.setPrefix(s);
    } else {
      tracker.set(s);
    }
  }

  const results = tracker.getAll();
  const phonemes = applyGlue(results, modifier, affricates);
  return { type, hasUnknown: tracker.getHasUnknown(), phonemes };
}

// keep track of current phoneme, which might have diacritics
class TrackCurrent {
  private phonemes: PhonemeItem[] = [];
  private current = '';
  private meta?: MetaType = undefined;
  private hasUnknown = false;
  private isPrefix = false;

  set(s: string, meta?: MetaType): void {
    if (this.meta === 'space' && (s === ',' || s === '.')) {
      this.useCurrent();
      this.current = s;
      this.meta = 'pause';
    } else if (meta === 'unknown' || meta === 'newline') {
      this.useCurrent();
      this.phonemes.push({ phoneme: s, meta });
      this.hasUnknown = (meta === 'unknown');
    } else if (meta === 'space') {
      if (this.current !== ' ') {
        this.useCurrent();
      }
      this.current = s;
      this.meta = meta;
    } else if (this.isPrefix) {
      this.current += s;
      this.isPrefix = false;
    } else {
      this.current = s;
      this.meta = meta;
      this.isPrefix = false;
    }
  }

  setPrefix(pre: string): void {
    this.current = pre;
    this.meta = undefined;
    this.isPrefix = true;
  }

  add(s: string): void {
    if (this.meta !== undefined) {
      // doesn't make sense to add to a meta symbol
      this.useCurrent();
    }
    this.current += s;
  }

  /** add syllable break or a pause, depending on context */
  addDot(): void {
    if (this.meta === undefined || this.meta === 'stress') {
      // we're in a word, so do a syllable break
      this.useCurrent();
      this.set('.', 'break');
    } else {
      this.set('.', 'pause');
    }
  }

  useCurrent(): void {
    if (this.current.length === 0) {
      return;
    }
    if (this.meta) {
      this.phonemes.push({ phoneme: this.current, meta: this.meta });
    } else {
      this.phonemes.push({ phoneme: this.current });
    }
    this.current = '';
    this.meta = undefined;
    this.isPrefix = false;
  }

  hasCurrent(): boolean {
    return this.current.length > 0;
  }

  currentCanHaveDiacritic(): boolean {
    return !isStress(this.current) && !isTone(this.current)
      && !PhonemeValidator.get().isIpaModifier(this.current);
  }

  hasPrefix(): boolean {
    return this.isPrefix;
  }

  getAll(): PhonemeItem[] {
    this.useCurrent();
    return this.phonemes;
  }

  getHasUnknown(): boolean {
    return this.hasUnknown;
  }
}


/** quick check for whether phonemes symbols are valid */
class PhonemeValidator {
  private synthSymbol: Set<string> = new Set<string>();
  private synthDiacritic: Set<string> = new Set<string>();
  private synthPrefix: Set<string> = new Set<string>();
  private ipaSymbol: Set<string> = new Set<string>();
  private ipaDiacritic: Set<string> = new Set<string>();
  private ipaModifier: Set<string> = new Set<string>();
  private ipaPlus: Set<string> = new Set<string>();

  static get(): PhonemeValidator {
    if (!this.validator) {
      this.validator = new PhonemeValidator();
    }
    return this.validator;
  }
  private static validator?: PhonemeValidator;

  constructor() {
    synthSymbols.forEach(a => this.synthSymbol.add(a));
    synthPost.forEach(a => this.synthDiacritic.add(a));
    synthPre.forEach(a => this.synthPrefix.add(a));

    const ipa = getAllIpa();
    ipa.ipa.forEach(a => this.ipaSymbol.add(a));
    ipa.diacritics.forEach(a => this.ipaDiacritic.add(a));
    getAllModifiers().forEach(a => this.ipaModifier.add(a));
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].forEach(a => this.ipaPlus.add(a.toString()));
  }

  isSynthSymbol(s: string) {
    return this.synthSymbol.has(s);
  }
  isSynthDiacritic(s: string) {
    return this.synthDiacritic.has(s);
  }
  isSynthPrefix(s: string) {
    return this.synthPrefix.has(s);
  }

  /** if plus, include length numbers */
  isIpaSymbol(s: string, plus: boolean) {
    return this.ipaSymbol.has(s) || (plus ? this.ipaPlus.has(s) : false);
  }
  isIpaDiacritic(s: string) {
    return this.ipaDiacritic.has(s);
  }
  isIpaModifier(s: string) {
    return this.ipaModifier.has(s);
  }
  isIpaPlus(s: string) {
    return this.ipaPlus.has(s);
  }

  isDelimiter(s: string) {
    const features = getFeaturesForOne(s);
    return features.includes('delimit');
  }
}

/** glue together phonemes when there's a combining symbol between them or for affricates or tones or "plus" numbers */
function applyGlue(input: PhonemeItem[], modifier: boolean, affricates?: 'all-aff' | 'en-aff'): PhonemeItem[] {
  const output: PhonemeItem[] = [];
  const n = input.length;
  for (let i = 0; i < n; i++) {
    if (shouldCombine(input, i)) {
      const combined = input[i].phoneme + input[i + 1].phoneme + input[i + 2].phoneme;
      output.push({ phoneme: combined });
      i += 2;
    } else if (affricates && isAffricate(input, i, affricates)) {
      const combined = input[i].phoneme + input[i + 1].phoneme;
      output.push({ phoneme: combined });
      i += 1;
    } else if (isTone(input[i].phoneme) && i < n - 1 && isTone(input[i + 1].phoneme)) {
      const third = i < n - 2 && isTone(input[i + 2].phoneme) ? input[i + 2].phoneme : '';
      const combined = input[i].phoneme + input[i + 1].phoneme + third;
      output.push({ phoneme: combined });
      i += third === '' ? 1 : 2;
    } else if (modifier && i < n - 1 && PhonemeValidator.get().isIpaModifier(input[i + 1].phoneme)) {
      const [modifiers, lenM] = getModifiers(input, i + 1, n);
      const [diacritics, lenD] = getDiacritics(input, i + 1 + lenM, n);
      const combined = input[i].phoneme + modifiers + diacritics;
      output.push({ phoneme: combined });
      i += lenM + lenD;
    } else if (i < n - 1 && PhonemeValidator.get().isIpaPlus(input[i].phoneme)
      && PhonemeValidator.get().isIpaPlus(input[i + 1].phoneme)) {
      // 2 or more "plus" values in a row, combine them all
      const result = getMultiDigits(input, i, n);
      output.push({ phoneme: result.combined });
      i = result.last;
    } else {
      output.push(input[i]);
    }
  }
  return output;
}

/** true if [i+1] says we should combine [i] & [i+2] */
function shouldCombine(input: PhonemeItem[], i: number): boolean {
  if (i < 0 || i + 2 >= input.length) {
    return false; // out of bounds
  } else if (input[i].meta || input[i + 2].meta) {
    return false; // symbols can't be validly combined
  }
  const features = getFeaturesForOne(input[i + 1].phoneme);
  return features.includes('combine');
}

/** true if [i]+[i+1] is one of the affricates we're interested in */
function isAffricate(input: PhonemeItem[], i: number, affricates: 'all-aff' | 'en-aff'): boolean {
  if (i < 0 || i + 1 >= input.length) {
    return false; // out of bounds
  } else if (input[i].meta || input[i + 1].meta) {
    return false; // symbols can't be validly combined
  }
  const a = input[i];
  const b = input[i + 1];
  const check = a.phoneme + b.phoneme[0]; // ignore b's diacritics for the check
  const features = getFeaturesForOne(check);
  if (features.includes('affricate')) {
    if (affricates === 'en-aff') {
      return ((a.phoneme === 't̠' || a.phoneme === 't') && b.phoneme === 'ʃ')
        || ((a.phoneme === 'd̠' || a.phoneme === 'd') && b.phoneme === 'ʒ');
    }
    return true;
  }
  return false;
}

/** get consecutive modifiers starting with input[start] */
function getModifiers(input: PhonemeItem[], start: number, n: number): [string, number] {
  let all = '';
  let total = 0;
  for (let i = start; i < n; i++) {
    if (!PhonemeValidator.get().isIpaModifier(input[i].phoneme)) {
      break;
    }
    all += input[i].phoneme;
    total++;
  }
  return [all, total];
}

/** get consecutive diacritics starting with input[start] */
function getDiacritics(input: PhonemeItem[], start: number, n: number): [string, number] {
  let all = '';
  let total = 0;
  for (let i = start; i < n; i++) {
    if (!PhonemeValidator.get().isIpaDiacritic(input[i].phoneme)) {
      break;
    }
    all += input[i].phoneme;
    total++;
  }
  return [all, total];
}

/** input[start] & [start+1] are both digits, find all the digits & combine them */
function getMultiDigits(input: PhonemeItem[], start: number, end: number)
  : { combined: string, last: number } {
  // 2 or more "plus" values in a row, combine them all
  let j = start + 2;
  for (; j < end; j++) {
    if (!PhonemeValidator.get().isIpaPlus(input[j].phoneme)) {
      break;
    }
  }
  let combined = '';
  for (let k = start; k < j; k++) {
    combined += input[k].phoneme;
  }
  return { combined, last: j - 1 };
}
